import type { ID, ProductDraft, PurchaseCostLine, UploadedFile } from "@/lib/catalog/types";
import {
  mapDealPartnersFromApi,
  mapShowroomChargeFromApi,
  serializeDealPartnersForApi,
  serializeShowroomChargeForApi,
  type ProductDealType,
  type ProductProfitRuleType,
} from "@/lib/catalog/dealPartners";
import { api } from "@/lib/apiClient";
import type { ProfitDistributionSummary } from "@/lib/sales/saleInvoiceStore";
import { attachNormalizedPurchaseFields, computeLandedCost } from "@/lib/catalog/purchaseCost";
import { normalizeProductMediaUrlForBrowser } from "@/lib/mediaUrls";
import { getStoredAuthUser } from "@/lib/authUser";
import { isAxiosError } from "axios";

/** Latest sale row returned with product detail when `inventory_status` is sold. */
export type ProductLatestSaleSummary = {
  id: number;
  invoice_no?: string;
  date: string | null;
  payable_date: string | null;
  delivery_date: string | null;
  total_amount: number;
  description?: string | null;
  customer?: { id: number; name: string; phone?: string | null } | null;
  employee?: { id: number; full_name?: string | null; employee_code?: string | null } | null;
  paid_total: number;
  outstanding: number;
  payment_status: string;
  token_amount: number;
  balance_amount: number;
  is_installment: boolean;
  installment_count: number | null;
  payments: Array<{
    id: number;
    amount: number;
    debit: number;
    credit: number;
    direction: "debit" | "credit";
    transaction_type: string | null;
    transaction_type_label: string | null;
    payment_date: string | null;
    payment_method: string | null;
    payment_method_label: string | null;
    notes?: string | null;
  }>;
  profit_distributions: Array<{
    id: number;
    partner_id: number;
    party_id: number | null;
    party_name: string | null;
    investment_amount: number | null;
    purchase_ratio: number | null;
    profit_ratio: number | null;
    profit_amount: number;
    final_payable: number;
    paid_amount: number;
    balance: number;
    status: string;
  }>;
  profit_distribution_summary?: ProfitDistributionSummary | null;
};

export type InventoryStatusLogRow = {
  id: number;
  event: string;
  eventLabel: string;
  oldStatus: string | null;
  newStatus: string | null;
  reason: string | null;
  changedBy: number | null;
  changedByName: string | null;
  createdAt: string | null;
};

export interface ProductRecord extends ProductDraft {
  id: ID;
  createdAt: string;
  /** Present on product show API when the unit is sold. */
  latestSale?: ProductLatestSaleSummary | null;
  /** Present on product show / status-change responses. */
  inventoryStatusLogs?: InventoryStatusLogRow[];
}

const LS_KEY = "inventory.products.v1";
const PRODUCTS_LIST_PATH = "/api/v1/catalog/products";

export function catalogProductErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    if (error.response?.status === 413) {
      return "Upload is too large for one request. Images are uploaded one at a time — try again or refresh the page.";
    }
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong. Please try again.";
}

type ProductApiRecord = {
  id?: string | number;
  category_id?: string | number | null;
  brand_id?: string | number | null;
  vehicle_model_id?: string | number | null;
  vehicle_version_id?: string | number | null;
  model_year?: string | number | null;
  supplier_id?: string | number | null;
  warehouse_id?: string | number | null;
  name?: string | null;
  slug?: string | null;
  description?: string | null;
  inventory_status?: string | null;
  purchase_price?: number | string | null;
  purchase_currency?: string | null;
  purchase_currency_amount?: number | string | null;
  purchase_exchange_rate?: number | string | null;
  purchase_notes?: string | null;
  purchase_cost_lines?: Array<{
    type_id?: string | number;
    type_label?: string | null;
    amount?: number | string;
    currency_code?: string | null;
    currency_amount?: number | string | null;
    exchange_rate?: number | string | null;
    paid_by_party_id?: number | string | null;
    payment_method?: string | null;
    bank_name?: string | null;
    transaction_reference_no?: string | null;
    pay_order_no?: string | null;
    description?: string | null;
  }> | null;
  landed_cost?: number | string | null;
  sale_price?: number | string | null;
  expected_sale_price?: number | string | null;
  owner_demand_price?: number | string | null;
  deal_type?: string | null;
  profit_rule_type?: string | null;
  deal_partners?: unknown;
  product_images?: unknown;
  product_documents?: unknown;
  document_type?: string | null;
  number_of_documents?: number | string | null;
  attributes?: Record<string, unknown> | string | null;
  created_at?: string | null;
  updated_at?: string | null;
  latest_sale?: unknown;
  inventory_status_logs?: unknown;
  showroom_charge?: unknown;
  /** @deprecated API legacy key */
  showroom_commission?: unknown;
};

type UpsertProductApiResponse = { message?: string; data?: ProductApiRecord };
type DeleteProductApiResponse = { message?: string };

function normalizeUploadedList(items: UploadedFile[] | undefined): UploadedFile[] {
  return (items ?? []).map((u) => ({
    ...u,
    url: normalizeProductMediaUrlForBrowser(u.url),
  }));
}

function normalizeProductRecordMedia(record: ProductRecord): ProductRecord {
  return {
    ...record,
    productImages: normalizeUploadedList(record.productImages),
    productDocuments: normalizeUploadedList(record.productDocuments),
  };
}

function load(): ProductRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as ProductRecord[];
    if (!Array.isArray(parsed)) return [];
    return parsed.map(normalizeProductRecordMedia);
  } catch {
    return [];
  }
}

function save(items: ProductRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(items.map(stripLocalFilesFromRecord)));
}

function replaceOrInsertProduct(record: ProductRecord) {
  const items = load();
  const idx = items.findIndex((p) => p.id === record.id);
  if (idx >= 0) items[idx] = record;
  else items.unshift(record);
  save(items);
}

function removeProductFromCache(id: ID): boolean {
  const items = load();
  const next = items.filter((p) => p.id !== id);
  if (next.length === items.length) return false;
  save(next);
  return true;
}

function extractRowsFromResponse(body: unknown): ProductApiRecord[] {
  if (Array.isArray(body)) return body as ProductApiRecord[];
  if (!body || typeof body !== "object") return [];
  const root = body as Record<string, unknown>;
  const fromBlock = (block: unknown): ProductApiRecord[] | null => {
    if (Array.isArray(block)) return block as ProductApiRecord[];
    if (block && typeof block === "object" && !Array.isArray(block)) {
      const inner = (block as Record<string, unknown>).data;
      if (Array.isArray(inner)) return inner as ProductApiRecord[];
    }
    return null;
  };
  for (const key of ["data", "products", "items", "results"]) {
    const hit = fromBlock(root[key]);
    if (hit) return hit;
  }
  return [];
}

export type ProductCatalogCursorMeta = {
  per_page: number;
  next_cursor: string | null;
  prev_cursor: string | null;
  has_more: boolean;
  has_previous: boolean;
};

export type ProductCatalogOffsetMeta = {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
};

function readCatalogOffsetMeta(body: unknown): ProductCatalogOffsetMeta {
  const def: ProductCatalogOffsetMeta = {
    current_page: 1,
    last_page: 1,
    per_page: 20,
    total: 0,
  };
  if (!body || typeof body !== "object") return def;
  const meta = (body as Record<string, unknown>).meta;
  if (!meta || typeof meta !== "object" || Array.isArray(meta)) return def;
  const m = meta as Record<string, unknown>;
  const current = Number(m.current_page ?? 1);
  const last = Number(m.last_page ?? 1);
  const per = Number(m.per_page ?? 20);
  const total = Number(m.total ?? 0);

  return {
    current_page: Number.isFinite(current) && current >= 1 ? current : 1,
    last_page: Number.isFinite(last) && last >= 1 ? last : 1,
    per_page: Number.isFinite(per) && per >= 1 ? per : 20,
    total: Number.isFinite(total) && total >= 0 ? total : 0,
  };
}

function readCatalogCursorMeta(body: unknown): ProductCatalogCursorMeta {
  const def: ProductCatalogCursorMeta = {
    per_page: 20,
    next_cursor: null,
    prev_cursor: null,
    has_more: false,
    has_previous: false,
  };
  if (!body || typeof body !== "object") return def;
  const meta = (body as Record<string, unknown>).meta;
  if (!meta || typeof meta !== "object" || Array.isArray(meta)) return def;
  const m = meta as Record<string, unknown>;
  return {
    per_page: Math.max(1, Number(m.per_page ?? 20)),
    next_cursor: typeof m.next_cursor === "string" && m.next_cursor !== "" ? m.next_cursor : null,
    prev_cursor: typeof m.prev_cursor === "string" && m.prev_cursor !== "" ? m.prev_cursor : null,
    has_more: Boolean(m.has_more),
    has_previous: Boolean(m.has_previous),
  };
}

export type FetchProductsCatalogParams = {
  cursor?: string | null;
  search?: string;
  category_id?: string;
  brand_id?: string;
  brand_none?: boolean;
  inventory_status?: string;
  sort?: string;
  model_year?: string;
  vehicle_model_id?: string;
  vehicle_version_id?: string;
  vehicle_brand_id?: string;
  vehicle_brand_none?: boolean;
  created_on?: string;
  created_from?: string;
  created_to?: string;
  min_purchase_price?: string;
  max_purchase_price?: string;
  min_landed_cost?: string;
  max_landed_cost?: string;
  attribute_filters?: string;
};

/** Offset page for the products UI (page numbers, total count). Does not write localStorage. */
export async function fetchProductsCatalogPage(
  params: Omit<FetchProductsCatalogParams, "cursor"> & { page: number; per_page: number },
): Promise<{ data: ProductRecord[]; meta: ProductCatalogOffsetMeta }> {
  const response = await api.get(PRODUCTS_LIST_PATH, {
    params: {
      page: Math.max(1, params.page),
      per_page: params.per_page,
      search: params.search?.trim() || undefined,
      category_id:
        params.category_id && params.category_id !== "__all__" ? params.category_id : undefined,
      brand_id:
        params.brand_id && params.brand_id !== "__all__" && params.brand_id !== "__none__"
          ? params.brand_id
          : undefined,
      brand_none: params.brand_none ? 1 : undefined,
      inventory_status: params.inventory_status?.trim() || undefined,
      sort: params.sort?.trim() || undefined,
      model_year:
        params.model_year && params.model_year !== "__all__" ? params.model_year : undefined,
      vehicle_model_id:
        params.vehicle_model_id && params.vehicle_model_id !== "__all__"
          ? params.vehicle_model_id
          : undefined,
      vehicle_version_id:
        params.vehicle_version_id && params.vehicle_version_id !== "__all__"
          ? params.vehicle_version_id
          : undefined,
      vehicle_brand_id:
        params.vehicle_brand_id && params.vehicle_brand_id !== "__all__" && params.vehicle_brand_id !== "__none__"
          ? params.vehicle_brand_id
          : undefined,
      vehicle_brand_none: params.vehicle_brand_none ? 1 : undefined,
      created_on: params.created_on?.trim() || undefined,
      created_from: params.created_from?.trim() || undefined,
      created_to: params.created_to?.trim() || undefined,
      min_purchase_price: params.min_purchase_price?.trim() || undefined,
      max_purchase_price: params.max_purchase_price?.trim() || undefined,
      min_landed_cost: params.min_landed_cost?.trim() || undefined,
      max_landed_cost: params.max_landed_cost?.trim() || undefined,
      attribute_filters: params.attribute_filters?.trim() || undefined,
    },
  });
  const body = response.data;
  const rows = extractRowsFromResponse(body);
  const data = rows.map((row) => mapProductFromApiPayload(row)).filter((r) => Boolean(r.id));
  return { data, meta: readCatalogOffsetMeta(body) };
}

/** Single cursor page (20 rows). Does not write localStorage. */
export async function fetchProductsCatalogCursor(
  params: FetchProductsCatalogParams,
): Promise<{ data: ProductRecord[]; meta: ProductCatalogCursorMeta }> {
  const response = await api.get(PRODUCTS_LIST_PATH, {
    params: {
      use_cursor: 1,
      cursor: params.cursor?.trim() || undefined,
      search: params.search?.trim() || undefined,
      category_id:
        params.category_id && params.category_id !== "__all__" ? params.category_id : undefined,
      brand_id:
        params.brand_id && params.brand_id !== "__all__" && params.brand_id !== "__none__"
          ? params.brand_id
          : undefined,
      brand_none: params.brand_none ? 1 : undefined,
      inventory_status: params.inventory_status?.trim() || undefined,
      sort: params.sort?.trim() || undefined,
      model_year:
        params.model_year && params.model_year !== "__all__" ? params.model_year : undefined,
      vehicle_model_id:
        params.vehicle_model_id && params.vehicle_model_id !== "__all__"
          ? params.vehicle_model_id
          : undefined,
      vehicle_version_id:
        params.vehicle_version_id && params.vehicle_version_id !== "__all__"
          ? params.vehicle_version_id
          : undefined,
      vehicle_brand_id:
        params.vehicle_brand_id && params.vehicle_brand_id !== "__all__" && params.vehicle_brand_id !== "__none__"
          ? params.vehicle_brand_id
          : undefined,
      vehicle_brand_none: params.vehicle_brand_none ? 1 : undefined,
      created_on: params.created_on?.trim() || undefined,
      created_from: params.created_from?.trim() || undefined,
      created_to: params.created_to?.trim() || undefined,
      min_purchase_price: params.min_purchase_price?.trim() || undefined,
      max_purchase_price: params.max_purchase_price?.trim() || undefined,
      min_landed_cost: params.min_landed_cost?.trim() || undefined,
      max_landed_cost: params.max_landed_cost?.trim() || undefined,
      attribute_filters: params.attribute_filters?.trim() || undefined,
    },
  });
  const body = response.data;
  const rows = extractRowsFromResponse(body);
  const data = rows.map((row) => mapProductFromApiPayload(row)).filter((r) => Boolean(r.id));
  return { data, meta: readCatalogCursorMeta(body) };
}

/** Merge snake_case and camelCase keys from Laravel / API resources. */
function normalizeProductApiRecord(raw: unknown): ProductApiRecord {
  if (!raw || typeof raw !== "object") return {};
  const o = raw as Record<string, unknown>;

  const linesRaw = o.purchase_cost_lines ?? o.purchaseCostLines;
  let purchase_cost_lines: ProductApiRecord["purchase_cost_lines"] = null;
  if (Array.isArray(linesRaw)) {
    purchase_cost_lines = linesRaw.map((row) => {
      if (!row || typeof row !== "object") return {};
      const r = row as Record<string, unknown>;
      return {
        type_id: r.type_id ?? r.typeId,
        type_label: r.type_label ?? r.typeLabel,
        amount: r.amount,
        currency_code: r.currency_code ?? r.currencyCode ?? null,
        currency_amount: r.currency_amount ?? r.currencyAmount ?? null,
        exchange_rate: r.exchange_rate ?? r.exchangeRate ?? null,
        paid_by_party_id: r.paid_by_party_id ?? r.paidByPartyId,
        payment_method: r.payment_method ?? r.paymentMethod,
        bank_name: r.bank_name ?? r.bankName,
        transaction_reference_no: r.transaction_reference_no ?? r.transactionReferenceNo,
        pay_order_no: r.pay_order_no ?? r.payOrderNo,
        description: r.description,
      };
    }) as NonNullable<ProductApiRecord["purchase_cost_lines"]>;
  }

  return {
    id: o.id,
    category_id: (o.category_id ?? o.categoryId) as string | number | null | undefined,
    brand_id: (o.brand_id ?? o.brandId) as string | number | null | undefined,
    vehicle_model_id: (o.vehicle_model_id ?? o.vehicleModelId) as string | number | null | undefined,
    vehicle_version_id: (o.vehicle_version_id ?? o.vehicleVersionId) as string | number | null | undefined,
    model_year: (o.model_year ?? o.modelYear) as string | number | null | undefined,
    supplier_id: (o.supplier_id ?? o.supplierId) as string | number | null | undefined,
    warehouse_id: (o.warehouse_id ?? o.warehouseId) as string | number | null | undefined,
    name: o.name as string | null | undefined,
    slug: o.slug as string | null | undefined,
    description: o.description as string | null | undefined,
    inventory_status: (o.inventory_status ?? o.inventoryStatus) as string | null | undefined,
    purchase_price: (o.purchase_price ?? o.purchasePrice) as number | string | null | undefined,
    purchase_currency: (o.purchase_currency ?? o.purchaseCurrency) as string | null | undefined,
    purchase_currency_amount: (o.purchase_currency_amount ?? o.purchaseCurrencyAmount) as number | string | null | undefined,
    purchase_exchange_rate: (o.purchase_exchange_rate ?? o.purchaseExchangeRate) as number | string | null | undefined,
    purchase_notes: (o.purchase_notes ?? o.purchaseNotes) as string | null | undefined,
    purchase_cost_lines,
    landed_cost: (o.landed_cost ?? o.landedCost) as number | string | null | undefined,
    sale_price: (o.sale_price ?? o.salePrice) as number | string | null | undefined,
    expected_sale_price: (o.expected_sale_price ?? o.expectedSalePrice) as number | string | null | undefined,
    owner_demand_price: (o.owner_demand_price ?? o.ownerDemandPrice) as number | string | null | undefined,
    deal_type: (o.deal_type ?? o.dealType) as string | null | undefined,
    profit_rule_type: (o.profit_rule_type ?? o.profitRuleType) as string | null | undefined,
    deal_partners: o.deal_partners ?? o.dealPartners,
    showroom_charge: o.showroom_charge ?? o.showroomCharge ?? o.showroom_commission ?? o.showroomCommission,
    product_images: o.product_images ?? o.productImages,
    product_documents: o.product_documents ?? o.productDocuments,
    document_type: (o.document_type ?? o.documentType) as string | null | undefined,
    number_of_documents: (o.number_of_documents ?? o.numberOfDocuments) as number | string | null | undefined,
    attributes: (o.attributes as ProductApiRecord["attributes"]) ?? null,
    created_at: (o.created_at ?? o.createdAt) as string | null | undefined,
    updated_at: (o.updated_at ?? o.updatedAt) as string | null | undefined,
    latest_sale: o.latest_sale ?? o.latestSale,
    inventory_status_logs: o.inventory_status_logs ?? o.inventoryStatusLogs,
  };
}

function mapProductFromApiPayload(raw: unknown): ProductRecord {
  return mapApiRecordToProduct(normalizeProductApiRecord(raw));
}

function parseAttributes(raw: ProductApiRecord["attributes"]): Record<string, import("@/lib/catalog/types").AttributeValue> {
  if (raw == null) return {};
  if (typeof raw === "string") {
    try {
      const o = JSON.parse(raw) as Record<string, import("@/lib/catalog/types").AttributeValue>;
      return o && typeof o === "object" && !Array.isArray(o) ? o : {};
    } catch {
      return {};
    }
  }
  if (typeof raw === "object" && !Array.isArray(raw)) return raw as Record<string, import("@/lib/catalog/types").AttributeValue>;
  return {};
}

function toStorageUrl(pathOrUrl: string | null | undefined): string | undefined {
  if (pathOrUrl == null) return undefined;
  const s = String(pathOrUrl).trim();
  if (!s) return undefined;
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith("data:")) return s;
  return s.startsWith("/") ? s : `/${s}`;
}

function resolveUploadUrlFromApiRow(o: Record<string, unknown>): string | undefined {
  const candidates = [
    o.url,
    o.file_url,
    o.fileUrl,
    o.image_url,
    o.imageUrl,
    o.photo_url,
    o.photoUrl,
    o.photoDataUrl,
    o.download_url,
    o.downloadUrl,
    o.public_url,
    o.publicUrl,
    o.path,
  ];
  for (const c of candidates) {
    if (typeof c === "string" && c.trim() !== "") {
      return toStorageUrl(c.trim());
    }
  }
  return undefined;
}

function mapUploadedFilesFromApi(raw: unknown): UploadedFile[] {
  if (!Array.isArray(raw)) return [];
  const out: UploadedFile[] = [];
  let fallbackIdx = 0;
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const o = item as Record<string, unknown>;
    const filename = String(o.filename ?? o.name ?? "");
    const id =
      o.id != null && String(o.id) !== ""
        ? String(o.id)
        : filename
          ? `file-${filename}-${fallbackIdx}`
          : `file-${fallbackIdx}`;
    fallbackIdx += 1;
    const copyRaw = o.document_copy ?? o.documentCopy;
    const copy =
      copyRaw === "duplicate" ? "duplicate" : copyRaw === "original" ? "original" : undefined;
    const typeIdRaw = o.document_type_id ?? o.documentTypeId;
    const typeNameRaw = o.document_type_name ?? o.documentTypeName;
    const pagesRaw = o.number_of_pages ?? o.numberOfPages;
    const pages =
      pagesRaw == null || pagesRaw === ""
        ? undefined
        : Number.isInteger(Number(pagesRaw))
          ? Math.min(100, Math.max(0, Number(pagesRaw)))
          : undefined;
    out.push({
      id,
      filename: filename || "file",
      mimeType: String(o.mime_type ?? o.mimeType ?? "application/octet-stream"),
      size: Number(o.size) || 0,
      visibility: o.visibility === "private" ? "private" : "public",
      url: normalizeProductMediaUrlForBrowser(resolveUploadUrlFromApiRow(o)),
      path: typeof o.path === "string" && o.path.trim() !== "" ? o.path.trim() : undefined,
      documentTypeId:
        typeIdRaw == null || typeIdRaw === "" ? undefined : String(typeIdRaw),
      documentTypeName:
        typeNameRaw == null || String(typeNameRaw).trim() === ""
          ? undefined
          : String(typeNameRaw).trim(),
      documentCopy: copy,
      numberOfPages: pages,
    });
  }
  return out;
}

function serializeUploadedFilesForApi(
  items: UploadedFile[] | undefined,
  documentMeta?: {
    documentTypeId?: string | null;
    documentTypeName?: string | null;
    documentCopy?: "original" | "duplicate" | null;
    numberOfPages?: number | null;
  },
): object[] {
  const typeId = documentMeta?.documentTypeId?.trim() || null;
  const typeName = documentMeta?.documentTypeName?.trim() || null;
  const copy =
    documentMeta?.documentCopy === "duplicate"
      ? "duplicate"
      : documentMeta?.documentCopy === "original"
        ? "original"
        : null;
  const pages =
    documentMeta?.numberOfPages == null || !Number.isFinite(Number(documentMeta.numberOfPages))
      ? null
      : Math.min(100, Math.max(0, Math.trunc(Number(documentMeta.numberOfPages))));

  return (items ?? []).map((f) => {
    const row: Record<string, unknown> = {
      id: f.id,
      filename: f.filename,
      mime_type: f.mimeType,
      size: f.size,
      visibility: f.visibility,
      path: f.path,
      url:
        f.url && !f.url.startsWith("blob:") && !f.url.startsWith("data:")
          ? f.url
          : undefined,
    };

    const rowTypeId = typeId ?? (f.documentTypeId ? String(f.documentTypeId) : null);
    const rowTypeName = typeName ?? (f.documentTypeName?.trim() || null);
    const rowCopy = copy ?? (f.documentCopy === "duplicate" || f.documentCopy === "original" ? f.documentCopy : null);
    const rowPages =
      pages ??
      (f.numberOfPages == null || !Number.isFinite(Number(f.numberOfPages))
        ? null
        : Math.min(100, Math.max(0, Math.trunc(Number(f.numberOfPages)))));

    if (rowTypeId && !rowTypeId.startsWith("seed-")) {
      const numericId = Number(rowTypeId);
      row.document_type_id = Number.isInteger(numericId) ? numericId : rowTypeId;
    }
    if (rowTypeName) row.document_type_name = rowTypeName;
    if (rowCopy) row.document_copy = rowCopy;
    if (rowPages != null) row.number_of_pages = rowPages;

    return row;
  });
}

function mapInventoryStatusLogsFromApi(raw: unknown): InventoryStatusLogRow[] {
  if (!Array.isArray(raw)) return [];
  const out: InventoryStatusLogRow[] = [];
  for (const row of raw) {
    if (!row || typeof row !== "object") continue;
    const r = row as Record<string, unknown>;
    const id = Number(r.id);
    if (!Number.isFinite(id) || id <= 0) continue;
    const eventRaw = r.event;
    const eventLabelRaw = r.event_label ?? r.eventLabel;
    const oldRaw = r.old_status ?? r.oldStatus;
    const newRaw = r.new_status ?? r.newStatus;
    const changedByRaw = r.changed_by ?? r.changedBy;
    const changedByNameRaw = r.changed_by_name ?? r.changedByName;
    const createdAtRaw = r.created_at ?? r.createdAt;
    const reasonRaw = r.reason;
    const event = eventRaw == null || String(eventRaw).trim() === "" ? "inventory_status_changed" : String(eventRaw);
    out.push({
      id,
      event,
      eventLabel:
        eventLabelRaw == null || String(eventLabelRaw).trim() === ""
          ? historyEventLabel(event)
          : String(eventLabelRaw).trim(),
      oldStatus: oldRaw == null || oldRaw === "" ? null : String(oldRaw),
      newStatus: newRaw == null || newRaw === "" ? null : String(newRaw),
      reason:
        reasonRaw == null || String(reasonRaw).trim() === "" ? null : String(reasonRaw).trim(),
      changedBy:
        changedByRaw == null || changedByRaw === ""
          ? null
          : Number.isFinite(Number(changedByRaw))
            ? Number(changedByRaw)
            : null,
      changedByName:
        changedByNameRaw == null || String(changedByNameRaw).trim() === ""
          ? null
          : String(changedByNameRaw).trim(),
      createdAt: createdAtRaw == null || createdAtRaw === "" ? null : String(createdAtRaw),
    });
  }
  return out;
}

function historyEventLabel(event: string): string {
  switch (event) {
    case "product_created":
      return "Product Created";
    case "product_updated":
      return "Product Updated";
    case "inventory_status_changed":
      return "Inventory Status Changed";
    case "reserved":
      return "Reserved";
    case "unsold":
      return "Unsold";
    case "sold":
      return "Sold";
    case "sale_cancelled":
      return "Sale Cancelled";
    default:
      return event.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }
}

function mapLatestSaleFromApi(raw: unknown): ProductLatestSaleSummary | null {
  if (!raw || typeof raw !== "object") return null;
  const s = raw as Record<string, unknown>;
  const id = Number(s.id);
  if (!Number.isFinite(id) || id <= 0) return null;
  const total = Number(s.total_amount);
  if (!Number.isFinite(total)) return null;

  const custRaw = s.customer;
  const customer =
    custRaw && typeof custRaw === "object"
      ? (() => {
          const c = custRaw as Record<string, unknown>;
          const cid = Number(c.id);
          return {
            id: Number.isFinite(cid) ? cid : 0,
            name: String(c.name ?? ""),
            phone: c.phone == null || c.phone === "" ? null : String(c.phone),
          };
        })()
      : null;

  const empRaw = s.employee;
  const employee =
    empRaw && typeof empRaw === "object"
      ? (() => {
          const e = empRaw as Record<string, unknown>;
          const eid = Number(e.id);
          return {
            id: Number.isFinite(eid) ? eid : 0,
            full_name: e.full_name == null ? null : String(e.full_name),
            employee_code: e.employee_code == null ? null : String(e.employee_code),
          };
        })()
      : null;

  const payments = Array.isArray(s.payments)
    ? s.payments.map((p: any) => ({
        id: Number(p.id),
        amount: Number(p.amount ?? 0),
        debit: Number(p.debit ?? 0),
        credit: Number(p.credit ?? 0),
        direction: p.direction === "credit" ? ("credit" as const) : ("debit" as const),
        transaction_type: p.transaction_type ? String(p.transaction_type) : null,
        transaction_type_label: p.transaction_type_label ? String(p.transaction_type_label) : null,
        payment_date: p.payment_date ? String(p.payment_date) : null,
        payment_method: p.payment_method ? String(p.payment_method) : null,
        payment_method_label: p.payment_method_label ? String(p.payment_method_label) : null,
        notes: p.notes ? String(p.notes) : null,
      }))
    : [];

  const profit_distributions = Array.isArray(s.profit_distributions)
    ? s.profit_distributions.map((pd: any) => ({
        id: Number(pd.id),
        partner_id: Number(pd.partner_id),
        party_id: pd.party_id != null ? Number(pd.party_id) : null,
        party_name: pd.party_name ? String(pd.party_name) : null,
        investment_amount: pd.investment_amount != null ? Number(pd.investment_amount) : null,
        purchase_ratio: pd.purchase_ratio != null ? Number(pd.purchase_ratio) : null,
        profit_ratio: pd.profit_ratio != null ? Number(pd.profit_ratio) : null,
        profit_amount: Number(pd.profit_amount ?? 0),
        final_payable: Number(pd.final_payable ?? 0),
        paid_amount: Number(pd.paid_amount ?? 0),
        balance: Number(pd.balance ?? 0),
        status: String(pd.status ?? "pending"),
      }))
    : [];

  return {
    id,
    invoice_no: s.invoice_no == null ? undefined : String(s.invoice_no),
    date: s.date == null ? null : String(s.date),
    payable_date: s.payable_date == null ? null : String(s.payable_date),
    delivery_date: s.delivery_date == null ? null : String(s.delivery_date),
    total_amount: total,
    description: s.description == null || s.description === "" ? null : String(s.description),
    customer,
    employee,
    paid_total: Number(s.paid_total ?? 0),
    outstanding: Number(s.outstanding ?? 0),
    payment_status: String(s.payment_status ?? "unpaid"),
    token_amount: Number(s.token_amount ?? 0),
    balance_amount: Number(s.balance_amount ?? 0),
    is_installment: Boolean(s.is_installment),
    installment_count: s.installment_count != null ? Number(s.installment_count) : null,
    payments,
    profit_distributions,
    profit_distribution_summary: s.profit_distribution_summary ?? s.profitDistributionSummary ?? null,
  };
}

function mapPurchaseLinesFromApi(
  raw: ProductApiRecord["purchase_cost_lines"],
): PurchaseCostLine[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row) => {
      const typeId = row?.type_id != null ? String(row.type_id) : "";
      const amount = Number(row?.amount);
      if (!typeId || !Number.isFinite(amount) || amount < 0) return null;
      const rawLabel = row?.type_label ?? (row as { typeLabel?: unknown }).typeLabel;
      const typeLabel =
        rawLabel != null && String(rawLabel).trim() !== "" ? String(rawLabel).trim() : null;
      const methodRaw = row?.payment_method;
      const paymentMethod =
        methodRaw === "cash" || methodRaw === "online" || methodRaw === "pay_order" ? methodRaw : null;
      const strOrNull = (v: unknown) =>
        v == null || String(v).trim() === "" ? null : String(v).trim();

      const paidByPartyId =
        row?.paid_by_party_id != null && String(row.paid_by_party_id).trim() !== ""
          ? String(row.paid_by_party_id)
          : null;

      const currAmt = row?.currency_amount != null && row?.currency_amount !== "" ? Number(row.currency_amount) : null;
      const exRate = row?.exchange_rate != null && row?.exchange_rate !== "" ? Number(row.exchange_rate) : null;

      return {
        typeId,
        typeLabel,
        amount,
        currencyCode: strOrNull(row?.currency_code),
        currencyAmount: currAmt != null && Number.isFinite(currAmt) ? currAmt : null,
        exchangeRate: exRate != null && Number.isFinite(exRate) ? exRate : null,
        paidByPartyId,
        paymentMethod,
        bankName: strOrNull(row?.bank_name),
        transactionReferenceNo: strOrNull(row?.transaction_reference_no),
        payOrderNo: strOrNull(row?.pay_order_no),
        description: row.description == null || row.description === "" ? null : String(row.description),
      };
    })
    .filter(Boolean) as PurchaseCostLine[];
}

function serializePurchaseCostLineForApi(l: PurchaseCostLine) {
  const method = l.paymentMethod === "cash" || l.paymentMethod === "online" || l.paymentMethod === "pay_order"
    ? l.paymentMethod
    : null;
  const paidByPartyId =
    l.paidByPartyId != null && String(l.paidByPartyId).trim() !== ""
      ? Number(l.paidByPartyId)
      : null;
  return {
    type_id: l.typeId,
    amount: l.amount,
    currency_code: l.currencyCode?.trim() || null,
    currency_amount: l.currencyAmount != null && Number.isFinite(Number(l.currencyAmount)) ? Number(l.currencyAmount) : null,
    exchange_rate: l.exchangeRate != null && Number.isFinite(Number(l.exchangeRate)) ? Number(l.exchangeRate) : null,
    paid_by_party_id: paidByPartyId,
    payment_method: method,
    bank_name:
      method === "online" || method === "pay_order"
        ? l.bankName?.trim() || null
        : null,
    transaction_reference_no: method === "online" ? l.transactionReferenceNo?.trim() || null : null,
    pay_order_no: method === "pay_order" ? l.payOrderNo?.trim() || null : null,
    description: l.description ?? null,
  };
}

const INVENTORY_STATUS_VALUES = new Set(["draft", "in_stock", "reserved", "unsold", "sold"]);

function normalizeInventoryStatusFromApi(raw: unknown): ProductDraft["inventoryStatus"] {
  const s = raw == null || raw === "" ? "draft" : String(raw);
  // Legacy alias after rename hold → unsold.
  if (s === "hold") return "unsold";
  return (INVENTORY_STATUS_VALUES.has(s) ? s : "draft") as ProductDraft["inventoryStatus"];
}

function mapApiRecordToProduct(record: ProductApiRecord): ProductRecord {
  const status = normalizeInventoryStatusFromApi(record.inventory_status);
  const basePurchase =
    record.purchase_price == null || record.purchase_price === ""
      ? null
      : Number(record.purchase_price);
  const landed =
    record.landed_cost == null || record.landed_cost === ""
      ? null
      : Number(record.landed_cost);
  const salePrice =
    record.sale_price == null || record.sale_price === ""
      ? null
      : Number(record.sale_price);
  const expectedSalePrice =
    record.expected_sale_price == null || record.expected_sale_price === ""
      ? null
      : Number(record.expected_sale_price);
  const ownerDemandPrice =
    record.owner_demand_price == null || record.owner_demand_price === ""
      ? null
      : Number(record.owner_demand_price);
  const dealTypeRaw = record.deal_type;
  const dealType: ProductDealType | null =
    dealTypeRaw === "owner_vehicle" || dealTypeRaw === "purchased" || dealTypeRaw === "partnership"
      ? dealTypeRaw
      : dealTypeRaw === "brokerage"
        ? "purchased"
        : null;
  const profitRuleRaw = record.profit_rule_type;
  const profitRuleType: ProductProfitRuleType | null =
    profitRuleRaw === "owner_surplus" ||
    profitRuleRaw === "commission" ||
    profitRuleRaw === "profit_sharing"
      ? profitRuleRaw
      : profitRuleRaw === "fixed_commission" || profitRuleRaw === "percentage_commission"
        ? "commission"
        : null;

  return {
    id: String(record.id ?? ""),
    categoryId: String(record.category_id ?? ""),
    brandId: record.brand_id != null && String(record.brand_id) !== "" ? String(record.brand_id) : null,
    vehicleModelId:
      record.vehicle_model_id != null && String(record.vehicle_model_id) !== ""
        ? String(record.vehicle_model_id)
        : null,
    vehicleVersionId:
      record.vehicle_version_id != null && String(record.vehicle_version_id) !== ""
        ? String(record.vehicle_version_id)
        : null,
    modelYear: (() => {
      const raw = record.model_year;
      if (raw == null || raw === "") return null;
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    })(),
    supplierId: record.supplier_id != null && String(record.supplier_id) !== "" ? String(record.supplier_id) : null,
    warehouseId:
      record.warehouse_id != null && String(record.warehouse_id) !== "" ? String(record.warehouse_id) : null,
    name: String(record.name ?? ""),
    slug: String(record.slug ?? ""),
    description: record.description == null ? null : String(record.description),
    inventoryStatus: status,
    purchasePrice: basePurchase != null && Number.isFinite(basePurchase) ? basePurchase : null,
    purchaseCurrency: record.purchase_currency == null ? null : String(record.purchase_currency),
    purchaseCurrencyAmount:
      record.purchase_currency_amount != null && Number.isFinite(Number(record.purchase_currency_amount))
        ? Number(record.purchase_currency_amount)
        : null,
    purchaseExchangeRate:
      record.purchase_exchange_rate != null && Number.isFinite(Number(record.purchase_exchange_rate))
        ? Number(record.purchase_exchange_rate)
        : null,
    purchaseNotes: record.purchase_notes == null ? null : String(record.purchase_notes),
    purchaseCostLines: mapPurchaseLinesFromApi(record.purchase_cost_lines),
    landedCost: landed != null && Number.isFinite(landed) ? landed : null,
    salePrice: salePrice != null && Number.isFinite(salePrice) ? salePrice : null,
    dealType,
    profitRuleType,
    expectedSalePrice:
      expectedSalePrice != null && Number.isFinite(expectedSalePrice) ? expectedSalePrice : null,
    ownerDemandPrice:
      ownerDemandPrice != null && Number.isFinite(ownerDemandPrice) ? ownerDemandPrice : null,
    ...(() => {
      const mapped = mapDealPartnersFromApi(record.deal_partners);
      const showroomCharge = mapShowroomChargeFromApi(
        record.showroom_charge ?? record.showroom_commission,
      );
      return {
        dealPartners: mapped.dealPartners,
        showroomCharge,
        companyOwnerCommission: showroomCharge,
      };
    })(),
    productImages: mapUploadedFilesFromApi(record.product_images),
    productDocuments: mapUploadedFilesFromApi(record.product_documents),
    // Form fields stay empty so another document can be added with new meta.
    documentCategoryId: null,
    documentCategory: null,
    documentType: "original",
    numberOfDocuments: undefined,
    attributes: parseAttributes(record.attributes),
    createdAt: String(record.created_at ?? new Date().toISOString()),
    latestSale: mapLatestSaleFromApi(record.latest_sale),
    inventoryStatusLogs: mapInventoryStatusLogsFromApi(record.inventory_status_logs),
  };
}

function productDraftHasPendingFiles(draft: ProductDraft): boolean {
  const imgs = draft.productImages ?? [];
  const docs = draft.productDocuments ?? [];
  return imgs.some((u) => u.localFile instanceof File) || docs.some((u) => u.localFile instanceof File);
}

function productDraftHasPendingImageFiles(draft: ProductDraft): boolean {
  return (draft.productImages ?? []).some((u) => u.localFile instanceof File);
}

function productDraftHasPendingDocFiles(draft: ProductDraft): boolean {
  return (draft.productDocuments ?? []).some((u) => u.localFile instanceof File);
}

export type ProductImageUploadCallbacks = {
  onFileProgress?: (fileId: string, percent: number) => void;
  onOverallProgress?: (completed: number, total: number) => void;
};

function extractImageFromUploadBody(body: unknown): Record<string, unknown> | null {
  if (!body || typeof body !== "object") return null;
  const root = body as Record<string, unknown>;
  const data = root.data;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    const image = (data as Record<string, unknown>).image;
    if (image && typeof image === "object" && !Array.isArray(image)) {
      return image as Record<string, unknown>;
    }
  }
  return null;
}

export async function uploadProductImage(
  productId: ID,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<UploadedFile> {
  const form = new FormData();
  form.append("image", file, file.name);
  const { data } = await api.post<unknown>(`${PRODUCTS_LIST_PATH}/${encodeURIComponent(productId)}/images`, form, {
    onUploadProgress: (event) => {
      if (!onProgress || !event.total) return;
      onProgress(Math.round((event.loaded / event.total) * 100));
    },
  });
  const row = extractImageFromUploadBody(data);
  if (!row) throw new Error("Invalid image upload response.");
  const mapped = mapUploadedFilesFromApi([row]);
  if (!mapped[0]) throw new Error("Invalid image upload response.");
  return mapped[0];
}

export async function ensureProductImagesUploaded(
  productId: ID,
  draft: ProductDraft,
  callbacks?: ProductImageUploadCallbacks,
): Promise<ProductDraft> {
  const images = draft.productImages ?? [];
  const pending = images.filter((u) => u.localFile instanceof File);
  if (pending.length === 0) return draft;

  const next: UploadedFile[] = [];
  let completed = 0;
  for (const u of images) {
    const local = u.localFile instanceof File ? u.localFile : null;
    if (!local) {
      next.push(u);
      continue;
    }
    const uploaded = await uploadProductImage(productId, local, (pct) => callbacks?.onFileProgress?.(u.id, pct));
    next.push(uploaded);
    completed += 1;
    callbacks?.onOverallProgress?.(completed, pending.length);
  }
  return { ...draft, productImages: next };
}

/** New files use `images[]` / `documents[]`. Existing rows stay in JSON `product_images` / `product_documents`. */
function appendProductMultipartFormData(form: FormData, draft: ProductDraft) {
  const norm = attachNormalizedPurchaseFields({
    ...draft,
    brandId: draft.brandId || null,
    supplierId: draft.supplierId || null,
    inventoryStatus: draft.inventoryStatus ?? "draft",
    productImages: draft.productImages ?? [],
    productDocuments: draft.productDocuments ?? [],
    attributes: draft.attributes ?? {},
  });
  const existingImages = (norm.productImages ?? []).filter((u) => !(u.localFile instanceof File));
  const existingDocs = (norm.productDocuments ?? []).filter((u) => !(u.localFile instanceof File));
  const newImageFiles = (norm.productImages ?? [])
    .filter((u) => u.localFile instanceof File)
    .map((u) => u.localFile as File);
  const newDocFiles = (norm.productDocuments ?? [])
    .filter((u) => u.localFile instanceof File)
    .map((u) => u.localFile as File);

  form.append("category_id", String(norm.categoryId));
  if (norm.brandId != null && String(norm.brandId).trim() !== "") {
    form.append("brand_id", String(norm.brandId));
  }
  if (norm.vehicleModelId != null && String(norm.vehicleModelId).trim() !== "") {
    form.append("vehicle_model_id", String(norm.vehicleModelId));
  }
  if (norm.vehicleVersionId != null && String(norm.vehicleVersionId).trim() !== "") {
    form.append("vehicle_version_id", String(norm.vehicleVersionId));
  }
  if (norm.modelYear != null && Number.isFinite(Number(norm.modelYear))) {
    form.append("model_year", String(norm.modelYear));
  }
  if (norm.supplierId != null && String(norm.supplierId).trim() !== "") {
    form.append("supplier_id", String(norm.supplierId));
  }
  if (norm.warehouseId != null && String(norm.warehouseId).trim() !== "") {
    form.append("warehouse_id", String(norm.warehouseId));
  }
  form.append("name", norm.name.trim());
  form.append("slug", norm.slug.trim());
  const desc = norm.description?.trim();
  if (desc) form.append("description", desc);
  form.append("inventory_status", norm.inventoryStatus ?? "draft");
  if (norm.purchasePrice != null && Number.isFinite(Number(norm.purchasePrice))) {
    form.append("purchase_price", String(norm.purchasePrice));
  }
  if (norm.purchaseCurrency != null && String(norm.purchaseCurrency).trim() !== "") {
    form.append("purchase_currency", String(norm.purchaseCurrency));
  }
  if (norm.purchaseCurrencyAmount != null && Number.isFinite(Number(norm.purchaseCurrencyAmount))) {
    form.append("purchase_currency_amount", String(norm.purchaseCurrencyAmount));
  }
  if (norm.purchaseExchangeRate != null && Number.isFinite(Number(norm.purchaseExchangeRate))) {
    form.append("purchase_exchange_rate", String(norm.purchaseExchangeRate));
  }
  const notes = norm.purchaseNotes?.trim();
  if (notes) form.append("purchase_notes", notes);
  if (norm.landedCost != null && Number.isFinite(Number(norm.landedCost))) {
    form.append("landed_cost", String(norm.landedCost));
  }
  if (norm.salePrice != null && Number.isFinite(Number(norm.salePrice))) {
    form.append("sale_price", String(norm.salePrice));
  }
  if (norm.dealType) form.append("deal_type", String(norm.dealType));
  // Always send so updates can clear Profit Rule (e.g. Purchased).
  form.append("profit_rule_type", norm.profitRuleType ? String(norm.profitRuleType) : "");
  if (norm.expectedSalePrice != null && Number.isFinite(Number(norm.expectedSalePrice))) {
    form.append("expected_sale_price", String(norm.expectedSalePrice));
  }
  form.append(
    "owner_demand_price",
    norm.ownerDemandPrice != null && Number.isFinite(Number(norm.ownerDemandPrice))
      ? String(norm.ownerDemandPrice)
      : "",
  );
  form.append(
    "deal_partners",
    JSON.stringify(
      serializeDealPartnersForApi(
        norm.dealPartners,
        norm.profitRuleType,
        norm.dealType,
        norm.landedCost ?? computeLandedCost(norm.purchasePrice, norm.purchaseCostLines),
      ),
    ),
  );
  form.append(
    "showroom_charge",
    JSON.stringify(
      serializeShowroomChargeForApi(
        norm.showroomCharge ?? norm.companyOwnerCommission,
        norm.purchasePrice,
      ),
    ),
  );
  form.append(
    "purchase_cost_lines",
    JSON.stringify((norm.purchaseCostLines ?? []).map((l) => serializePurchaseCostLineForApi(l))),
  );
  form.append("attributes", JSON.stringify(norm.attributes ?? {}));
  form.append("product_images", JSON.stringify(serializeUploadedFilesForApi(existingImages)));
  form.append("product_documents", JSON.stringify(serializeUploadedFilesForApi(existingDocs)));
  const newDocEntries = (norm.productDocuments ?? []).filter((u) => u.localFile instanceof File);
  form.append(
    "documents_meta",
    JSON.stringify(
      newDocEntries.map((u) => {
        const typeId = u.documentTypeId ? String(u.documentTypeId) : "";
        const pages =
          u.numberOfPages == null || !Number.isFinite(Number(u.numberOfPages))
            ? 0
            : Math.min(100, Math.max(0, Math.trunc(Number(u.numberOfPages))));
        return {
          document_type_id:
            typeId && !typeId.startsWith("seed-") && Number.isInteger(Number(typeId))
              ? Number(typeId)
              : null,
          document_type_name: u.documentTypeName?.trim() || null,
          document_copy: u.documentCopy === "duplicate" ? "duplicate" : "original",
          number_of_pages: pages,
        };
      }),
    ),
  );
  form.append(
    "document_type",
    existingDocs[0]?.documentCopy === "duplicate" || newDocEntries[0]?.documentCopy === "duplicate"
      ? "duplicate"
      : "original",
  );
  form.append(
    "number_of_documents",
    String(
      (() => {
        const n = Number(
          existingDocs[0]?.numberOfPages ?? newDocEntries[0]?.numberOfPages ?? 0,
        );
        if (!Number.isInteger(n) || n < 0) return 0;
        return Math.min(100, n);
      })(),
    ),
  );

  for (const file of newImageFiles) {
    form.append("images[]", file, file.name);
  }
  for (const file of newDocFiles) {
    form.append("documents[]", file, file.name);
  }
}

function stripLocalFilesFromRecord(p: ProductRecord): ProductRecord {
  return {
    ...p,
    productImages: (p.productImages ?? []).map(({ localFile: _f, ...rest }) => ({ ...rest })),
    productDocuments: (p.productDocuments ?? []).map(({ localFile: _f, ...rest }) => ({ ...rest })),
  };
}

function buildProductJsonBody(draft: ProductDraft) {
  const norm = attachNormalizedPurchaseFields({
    ...draft,
    brandId: draft.brandId || null,
    supplierId: draft.supplierId || null,
    inventoryStatus: draft.inventoryStatus ?? "draft",
    productImages: draft.productImages ?? [],
    productDocuments: draft.productDocuments ?? [],
    attributes: draft.attributes ?? {},
  });

  return {
    category_id: norm.categoryId,
    brand_id: norm.brandId,
    vehicle_model_id: norm.vehicleModelId ?? null,
    vehicle_version_id: norm.vehicleVersionId ?? null,
    model_year: norm.modelYear ?? null,
    supplier_id: norm.supplierId,
    warehouse_id: norm.warehouseId ?? null,
    name: norm.name.trim(),
    slug: norm.slug.trim(),
    description: norm.description?.trim() || null,
    inventory_status: norm.inventoryStatus ?? "draft",
    purchase_price: norm.purchasePrice,
    purchase_currency: norm.purchaseCurrency ?? null,
    purchase_currency_amount: norm.purchaseCurrencyAmount ?? null,
    purchase_exchange_rate: norm.purchaseExchangeRate ?? null,
    purchase_notes: norm.purchaseNotes ?? null,
    purchase_cost_lines: (norm.purchaseCostLines ?? []).map((l) => serializePurchaseCostLineForApi(l)),
    landed_cost: norm.landedCost,
    sale_price: norm.salePrice,
    deal_type: norm.dealType ?? null,
    profit_rule_type: norm.profitRuleType ?? null,
    expected_sale_price: norm.expectedSalePrice ?? null,
    owner_demand_price: norm.ownerDemandPrice ?? null,
    deal_partners: serializeDealPartnersForApi(
      norm.dealPartners,
      norm.profitRuleType,
      norm.dealType,
      norm.landedCost ?? computeLandedCost(norm.purchasePrice, norm.purchaseCostLines),
    ),
    showroom_charge: serializeShowroomChargeForApi(
      norm.showroomCharge ?? norm.companyOwnerCommission,
      norm.purchasePrice,
    ),
    product_images: serializeUploadedFilesForApi(norm.productImages),
    product_documents: serializeUploadedFilesForApi(norm.productDocuments),
    document_type: (() => {
      const first = (norm.productDocuments ?? [])[0];
      return first?.documentCopy === "duplicate" ? "duplicate" : "original";
    })(),
    number_of_documents: (() => {
      const first = (norm.productDocuments ?? [])[0];
      const n = Number(first?.numberOfPages ?? 0);
      if (!Number.isInteger(n) || n < 0) return 0;
      return Math.min(100, n);
    })(),
    attributes: norm.attributes,
  };
}

function extractProductFromUpsertBody(body: unknown): ProductApiRecord | null {
  if (!body || typeof body !== "object") return null;
  const root = body as Record<string, unknown>;
  const data = root.data;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    const block = data as Record<string, unknown>;
    if ("id" in block || typeof block.name === "string") {
      return block as unknown as ProductApiRecord;
    }
    const nested = block.product;
    if (nested && typeof nested === "object" && !Array.isArray(nested)) {
      return nested as unknown as ProductApiRecord;
    }
  }
  const top = root.product;
  if (top && typeof top === "object" && !Array.isArray(top)) {
    return top as unknown as ProductApiRecord;
  }
  if ("id" in root && root.id != null) {
    return root as unknown as ProductApiRecord;
  }
  return null;
}

export function listProducts(): ProductRecord[] {
  return load().sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export function getProductById(id: ID): ProductRecord | undefined {
  return load().find((p) => p.id === id);
}

export function productCountForBrand(brandId: string): number {
  return load().filter((p) => p.brandId === brandId).length;
}

export function countProductsUsingPurchaseCostType(typeId: ID): number {
  return load().filter((p) => p.purchaseCostLines?.some((l) => l.typeId === typeId)).length;
}

export function countProductsUsingWarehouse(warehouseId: ID): number {
  return load().filter((p) => p.warehouseId === warehouseId).length;
}

/** Replace local cache by walking cursor pages (for legacy screens that still hydrate localStorage). */
export async function syncProductsFromApi(): Promise<ProductRecord[]> {
  try {
    const collected: ProductApiRecord[] = [];
    let cursor: string | undefined;
    let guard = 0;
    while (guard < 500) {
      guard += 1;
      const response = await api.get(PRODUCTS_LIST_PATH, {
        params: { use_cursor: 1, cursor },
      });
      const body = response.data;
      collected.push(...extractRowsFromResponse(body));
      const meta = readCatalogCursorMeta(body);
      if (!meta.has_more || !meta.next_cursor) {
        break;
      }
      cursor = meta.next_cursor;
    }

    const records = collected.map((row) => mapProductFromApiPayload(row)).filter((r) => Boolean(r.id));
    save(records);
    return load();
  } catch (error) {
    if (!(isAxiosError(error) && error.response?.status === 403)) {
      console.error("[Products] syncProductsFromApi failed", error);
    }
    throw error;
  }
}

export async function createProductOnServer(
  draft: ProductDraft,
  uploadCallbacks?: ProductImageUploadCallbacks,
): Promise<ProductRecord> {
  const hasPendingImages = productDraftHasPendingImageFiles(draft);
  const hasPendingDocs = productDraftHasPendingDocFiles(draft);

  if (!hasPendingImages && !hasPendingDocs) {
    const body = buildProductJsonBody(draft);
    const response = await api.post<UpsertProductApiResponse>(PRODUCTS_LIST_PATH, body);
    const created = extractProductFromUpsertBody(response.data);
    if (!created || created.id == null) {
      throw new Error("Invalid create product response.");
    }
    const record = mapProductFromApiPayload(created);
    replaceOrInsertProduct(record);
    return record;
  }

  const stagedDraft: ProductDraft = {
    ...draft,
    productImages: (draft.productImages ?? []).filter((u) => !(u.localFile instanceof File)),
    productDocuments: (draft.productDocuments ?? []).filter((u) => !(u.localFile instanceof File)),
  };
  const createBody = buildProductJsonBody(stagedDraft);
  const createResponse = await api.post<UpsertProductApiResponse>(PRODUCTS_LIST_PATH, createBody);
  const created = extractProductFromUpsertBody(createResponse.data);
  if (!created || created.id == null) {
    throw new Error("Invalid create product response.");
  }
  const id = String(created.id);

  let workingDraft = await ensureProductImagesUploaded(id, draft, uploadCallbacks);

  let response: { data: UpsertProductApiResponse };
  if (productDraftHasPendingDocFiles(workingDraft)) {
    const form = new FormData();
    form.append("_method", "PUT");
    appendProductMultipartFormData(form, workingDraft);
    response = await api.post<UpsertProductApiResponse>(`${PRODUCTS_LIST_PATH}/${encodeURIComponent(id)}`, form);
  } else {
    const body = buildProductJsonBody(workingDraft);
    response = await api.put<UpsertProductApiResponse>(`${PRODUCTS_LIST_PATH}/${encodeURIComponent(id)}`, body);
  }

  let updated = extractProductFromUpsertBody(response.data);
  if (!updated) throw new Error("Invalid create product response.");
  if (updated.id == null || String(updated.id) === "") {
    updated = { ...updated, id };
  }
  const record = mapProductFromApiPayload(updated);
  replaceOrInsertProduct(record);
  return record;
}

export async function updateProduct(
  id: ID,
  draft: ProductDraft,
  uploadCallbacks?: ProductImageUploadCallbacks,
): Promise<ProductRecord> {
  const workingDraft = await ensureProductImagesUploaded(id, draft, uploadCallbacks);
  let response: { data: UpsertProductApiResponse };
  if (productDraftHasPendingDocFiles(workingDraft)) {
    const form = new FormData();
    form.append("_method", "PUT");
    appendProductMultipartFormData(form, workingDraft);
    response = await api.post<UpsertProductApiResponse>(`${PRODUCTS_LIST_PATH}/${encodeURIComponent(id)}`, form);
  } else {
    const body = buildProductJsonBody(workingDraft);
    response = await api.put<UpsertProductApiResponse>(`${PRODUCTS_LIST_PATH}/${encodeURIComponent(id)}`, body);
  }
  let updated = extractProductFromUpsertBody(response.data);
  if (!updated) throw new Error("Invalid update product response.");
  if (updated.id == null || String(updated.id) === "") {
    updated = { ...updated, id };
  }
  const record = mapProductFromApiPayload(updated);
  replaceOrInsertProduct(record);
  return record;
}

/** Sets inventory to in_stock (e.g. publish from catalog list). */
export async function publishProductToInStock(id: ID): Promise<ProductRecord> {
  const { data } = await api.put<UpsertProductApiResponse>(`${PRODUCTS_LIST_PATH}/${encodeURIComponent(id)}`, {
    inventory_status: "in_stock",
  });
  let updated = extractProductFromUpsertBody(data);
  if (!updated) throw new Error("Invalid publish product response.");
  if (updated.id == null || String(updated.id) === "") {
    updated = { ...updated, id };
  }
  const record = mapProductFromApiPayload(updated);
  replaceOrInsertProduct(record);
  return record;
}

/** Duplicate a product (and related data) as a new Draft. */
export async function copyProductAsDraft(
  id: ID,
  sections?: Array<"basic" | "additional" | "media" | "purchase">,
): Promise<ProductRecord> {
  const { data } = await api.post<UpsertProductApiResponse>(
    `${PRODUCTS_LIST_PATH}/${encodeURIComponent(id)}/copy-as-draft`,
    { sections: sections && sections.length > 0 ? sections : ["basic", "additional", "media", "purchase"] },
  );
  const created = extractProductFromUpsertBody(data);
  if (!created || created.id == null || String(created.id) === "") {
    throw new Error("Invalid copy product response.");
  }
  const record = mapProductFromApiPayload(created);
  replaceOrInsertProduct(record);
  return record;
}

export async function deleteProduct(id: ID): Promise<boolean> {
  await api.delete<DeleteProductApiResponse>(`${PRODUCTS_LIST_PATH}/${encodeURIComponent(id)}`);
  return removeProductFromCache(id);
}

/** Load one product from the API and merge into the local cache (deep links, refresh). */
export async function fetchProductFromApi(id: ID): Promise<ProductRecord | null> {
  try {
    const { data } = await api.get<unknown>(`${PRODUCTS_LIST_PATH}/${encodeURIComponent(id)}`);
    const extracted = extractProductFromUpsertBody(data);
    const raw =
      extracted ??
      (data && typeof data === "object"
        ? ((data as Record<string, unknown>).data ?? data)
        : null);
    if (!raw || typeof raw !== "object") return null;
    const o = raw as Record<string, unknown>;
    if (o.id === undefined || o.id === null || String(o.id) === "") return null;
    const record = mapProductFromApiPayload(raw);
    if (!record.id) return null;
    replaceOrInsertProduct(record);
    return record;
  } catch (e) {
    if (isAxiosError(e) && e.response?.status === 403) {
      throw e;
    }
    return null;
  }
}

export type InventoryStatusValue = NonNullable<ProductDraft["inventoryStatus"]>;

/** Change product inventory status and write an audit log row on the server. */
export async function changeProductInventoryStatus(
  id: ID,
  inventoryStatus: InventoryStatusValue,
  reason?: string | null,
): Promise<ProductRecord> {
  const { data } = await api.patch<unknown>(
    `${PRODUCTS_LIST_PATH}/${encodeURIComponent(id)}/inventory-status`,
    {
      inventory_status: inventoryStatus,
      reason: reason?.trim() ? reason.trim() : null,
    },
  );
  const extracted = extractProductFromUpsertBody(data);
  const raw =
    extracted ??
    (data && typeof data === "object" ? ((data as Record<string, unknown>).data ?? data) : null);
  if (!raw || typeof raw !== "object") {
    throw new Error("Invalid inventory status response.");
  }
  const record = mapProductFromApiPayload(raw);
  if (!record.id) {
    throw new Error("Invalid inventory status response.");
  }
  replaceOrInsertProduct(record);
  return record;
}

export async function persistProductWizardProgress(
  normalizedDraft: ProductDraft,
  existingId: ID | null,
): Promise<{ id: ID }> {
  if (existingId) {
    try {
      const updated = await updateProduct(existingId, normalizedDraft);
      return { id: updated.id };
    } catch (e) {
      // Stale wizard snapshot (e.g. sessionStorage id after API-only sync cleared local cache) or deleted draft.
      if (isAxiosError(e) && e.response?.status === 404) {
        const rec = await createProductOnServer(normalizedDraft);
        return { id: rec.id };
      }
      throw e;
    }
  }
  const rec = await createProductOnServer(normalizedDraft);
  return { id: rec.id };
}

export async function finalizeWizardProduct(
  normalizedDraft: ProductDraft,
  id: ID,
): Promise<ProductRecord> {
  return updateProduct(id, normalizedDraft);
}

const DEMO_PRODUCTS_BOOT_KEY = "inventory.products.demoSeeded.v1";

/** Inserts sample products when storage is empty (matches seeded category ids `cat_1` / `cat_2` and attribute codes). */
export function seedDemoProductsIfEmpty() {
  if (typeof localStorage === "undefined") return;
  if (localStorage.getItem(DEMO_PRODUCTS_BOOT_KEY)) return;
  if (load().length > 0) {
    localStorage.setItem(DEMO_PRODUCTS_BOOT_KEY, "1");
    return;
  }

  const base = Date.now();
  const samples: ProductRecord[] = [
    {
      id: "demo_prod_car_1",
      categoryId: "cat_1",
      brandId: "brand_1",
      name: "Toyota Corolla GLi 2024",
      slug: "toyota-corolla-gli-2024",
      description: "Local assembly, single owner, full service history.",
      createdAt: new Date(base - 86400000 * 7).toISOString(),
      attributes: {
        make: "Toyota",
        model: "Corolla GLi",
        year: 2024,
        assembly: "local",
        engine_cc: 1800,
        color: "White",
        features: ["abs", "airbags", "rear_camera"],
      },
    },
    {
      id: "demo_prod_car_2",
      categoryId: "cat_1",
      brandId: "brand_2",
      name: "Honda Civic Oriel 2023",
      slug: "honda-civic-oriel-2023",
      description: "Turbo, under warranty, non-accidental.",
      createdAt: new Date(base - 86400000 * 6).toISOString(),
      attributes: {
        make: "Honda",
        model: "Civic Oriel",
        year: 2023,
        assembly: "local",
        engine_cc: 1500,
        color: "Crystal Black",
        features: ["abs", "airbags", "alloy_wheels", "rear_camera"],
      },
    },
    {
      id: "demo_prod_car_3",
      categoryId: "cat_1",
      brandId: "brand_3",
      name: "Suzuki Alto VXR 2024",
      slug: "suzuki-alto-vxr-2024",
      description: "Fuel efficient city car, brand new condition.",
      createdAt: new Date(base - 86400000 * 5).toISOString(),
      attributes: {
        make: "Suzuki",
        model: "Alto VXR",
        year: 2024,
        assembly: "local",
        engine_cc: 660,
        color: "Silver",
        features: ["abs", "airbags"],
      },
    },
    {
      id: "demo_prod_car_4",
      categoryId: "cat_1",
      brandId: "brand_4",
      name: "KIA Sportage AWD 2023",
      slug: "kia-sportage-awd-2023",
      description: "Imported, AWD, panoramic roof.",
      createdAt: new Date(base - 86400000 * 4).toISOString(),
      attributes: {
        make: "KIA",
        model: "Sportage AWD",
        year: 2023,
        assembly: "imported",
        engine_cc: 2000,
        color: "Mars Orange",
        features: ["abs", "airbags", "rear_camera", "alloy_wheels"],
      },
    },
    {
      id: "demo_prod_part_1",
      categoryId: "cat_2",
      brandId: "brand_5",
      name: "Front brake pads set — Toyota Corolla 2014–2019",
      slug: "brake-pads-corolla-2014-2019",
      description: "OEM-spec ceramic pads, axle set.",
      createdAt: new Date(base - 86400000 * 3).toISOString(),
      attributes: {
        brand: "BrakePro",
        condition: "new",
        compatibility: "Toyota Corolla E170 2014–2019 (excluding hybrid)",
      },
    },
    {
      id: "demo_prod_part_2",
      categoryId: "cat_2",
      brandId: "brand_5",
      name: "Headlamp assembly (LHS) — Honda Civic 2016–2021",
      slug: "headlamp-lhs-civic-2016-2021",
      description: "Halogen type, with motor.",
      createdAt: new Date(base - 86400000 * 2).toISOString(),
      attributes: {
        brand: "Depo",
        condition: "new",
        compatibility: "Honda Civic FC 2016–2021, left hand traffic",
      },
    },
    {
      id: "demo_prod_part_3",
      categoryId: "cat_2",
      brandId: "brand_5",
      name: 'Used alloy wheel 16" — Suzuki Swift',
      slug: "alloy-wheel-16-swift-used",
      description: "Single wheel, minor curb marks.",
      createdAt: new Date(base - 86400000 * 1).toISOString(),
      attributes: {
        brand: "Generic",
        condition: "used",
        compatibility: "Suzuki Swift / Dzire 16x6J 5x114.3",
      },
    },
  ];

  save(samples);
  localStorage.setItem(DEMO_PRODUCTS_BOOT_KEY, "1");
}

/** Map API `product_images` / ad listing `images` JSON to uploaded file rows. */
export function mapProductImagesFromApi(raw: unknown): import("@/lib/catalog/types").UploadedFile[] {
  return mapUploadedFilesFromApi(raw);
}

/** Serialize product/ad listing images for API storage (matches products.product_images JSON). */
export function serializeProductImagesForApi(items: import("@/lib/catalog/types").UploadedFile[] | undefined): object[] {
  return serializeUploadedFilesForApi(items);
}
